import { mapGetters } from 'vuex'

import { cloneDeep, isEqual } from 'lodash'

import { Services, joinURLPath } from '@sigma-frontend-libs/commons'
import { SETTINGS_SITE_SCHEMA } from '@sigma-legacy-libs/essentials/lib/schemas/site'
import { SETTINGS_USER_SCHEMA } from '@sigma-legacy-libs/essentials/lib/schemas/user'

import { globalErrorHandler, icons, request, routerQueryReplace } from '@/utils'

const schemaIcons = {
  sendings: icons.message,
  billings: icons.monetization_on,
  referral: icons.group_work,
  websocket: icons.account_tree,
  smpp: icons.sms,
  webhooks: icons.link,
  notifications: icons.notifications,
  integrations: icons.extension,
  resellers: icons.group,
  auth: icons.vpn_key,
  frontend: icons.color_lens,
  payments: icons.payments,
  'otp-verification': icons['123']
}

// https://sigmasms.slack.com/archives/DDQ6HS91D/p1753963541666309?thread_ts=1753960887.431369&cid=DDQ6HS91D
const skipNotificationKeys = [
  'notifications.enabled',
  'notifications.events.billings.limit'
]
// https://sigmasms.slack.com/archives/C03RRLZB8MP/p1760943733512909?thread_ts=1760940702.480309&cid=C03RRLZB8MP
const skipSendingsKeys = [
  'sendings.sendernamesCheck',
  'sendings.patternsCheck'
]

// https://sigmasms.slack.com/archives/C03RRLZB8MP/p1760940702480309
const separatorKey = '__separator__'
const renderOrders = {
  sendings: [
    'sendings.phoneNumber.checkMobile',
    separatorKey,
    'sendings.allowDuplicates',
    'sendings.duplicates.hashFields',
    'sendings.duplicates.timeout',
    separatorKey,
    'sendings.rateLimit.maxAmount',
    'sendings.rateLimit.timeout',
    separatorKey,
    'sendings.tilda.sendFromForms'
  ],
  billings: [
    'billings.processChildrenOperations',
    'billings.billOnErrors',
    'billings.overdraft',
    'billings.currency',
    separatorKey,
    'billings.autoAccrual'
  ]
}
const sectionOrders = {
  [Services.settingsUser]: [
    'sendings',
    'billings',
    'referral',
    'websocket',
    'smpp',
    'webhooks',
    'notifications',
    'integrations',
    'reports'
  ],
  [Services.settingsSite]: [
    'resellers',
    'auth',
    'frontend',
    'notifications',
    'payments',
    'globalBlacklistManager',
    'integrations',
    'misc'
  ]
}

const getSchema = serviceName => {
  switch (serviceName) {
    case Services.settingsSite: return SETTINGS_SITE_SCHEMA
    case Services.settingsUser: return SETTINGS_USER_SCHEMA
  }
}
const getDefaults = serviceName => {
  const result = {}
  const schema = getSchema(serviceName)
  for (const [ key, { $default } ] of Object.entries(schema)) {
    result[key] = $default
  }

  return result
}

export const settingsSiteUser = options => {
  const { serviceName, UserId } = options || {}

  return {
    name: serviceName,

    data() {
      return {
        keys: undefined,
        settings: undefined,
        settingKey: this.$route?.query?.settingKey,
        loading: {
          find: false,
          update: {}
        }
      }
    },

    computed: {
      ...mapGetters({ token: 'authentication/token' }),

      schema() {
        if (!this.keys) {
          return []
        }

        const result = {}
        for (const key in this.keys) {
          if (key === 'apisender.userApiKey') {
            continue
          }
          if (skipNotificationKeys.includes(key)) {
            continue
          }
          if (skipSendingsKeys.includes(key)) {
            continue
          }
          switch (key) {
            default: {
              switch (serviceName) {
                case Services.settingsUser: {
                  const parts = key.split('.')
                  const service = parts[0]
                  if (!result[service]) {
                    result[service] = []
                  }
                  if (service === Services.notifications) {
                    const type = parts[1]
                    if (type === 'events' || type === 'childrenUsersEvents') {
                      const groupKey = `${Services.notifications}.events`
                      if (!result[service].includes(groupKey)) {
                        result[service].push(groupKey)
                      }
                    } else {
                      result[service].push(key)
                    }
                    continue
                  } else {
                    result[service].push(key)
                  }
                  break
                }
                case Services.settingsSite: {
                  const [ service, type, component ] = key.split('.')
                  if (!result[service]) {
                    result[service] = []
                  }
                  switch (service) {
                    case 'globalBlacklistManager': {
                      if (!result[service].includes(service)) {
                        result[service].push(service)
                      }
                      break
                    }
                    case 'notifications': {
                      const groupKeyByService = `${Services.notifications}.templates`
                      if (!result[service].includes(groupKeyByService)) {
                        result[service].push(groupKeyByService)
                      }
                      if (component !== 'templates') {
                        switch (type) {
                          case 'sms': {
                            const groupKeyByType = `${Services.notifications}.${type}`
                            if (!result[service].includes(groupKeyByType)) {
                              result[service].push(groupKeyByType)
                            }
                            break
                          }
                          default: {
                            result[service].push(key)
                            break
                          }
                        }
                      }
                      break
                    }
                    case 'otp-verification': {
                      const groupKeyByService = `${service}.user-phone`
                      if (!result[service].includes(groupKeyByService)) {
                        result[service].push(groupKeyByService)
                      }
                      break
                    }
                    case 'frontend': {
                      if (type === 'documents') {
                        const groupKeyByType = `${service}.documents`
                        if (!result[service].includes(groupKeyByType)) {
                          result[service].push(groupKeyByType)
                        }
                      } else {
                        if (!result[service].includes(key)) {
                          result[service].push(key)
                        }
                      }
                      break
                    }
                    default: {
                      result[service].push(key)
                      break
                    }
                  }
                  if (result.misc !== undefined) {
                    const misc = result.misc
                    delete result.misc
                    result.misc = misc
                  }
                }
              }
            }
          }
        }

        if (serviceName === Services.settingsUser) {
          for (const service of Object.keys(result)) {
            if (renderOrders[service]) {
              const original = result[service].slice()
              const order = renderOrders[service]
              const ordered = []
              for (const item of order) {
                if (item === separatorKey) {
                  ordered.push(separatorKey)
                } else if (original.includes(item)) {
                  ordered.push(item)
                }
              }
              const compacted = []
              for (const v of ordered) {
                if (v === separatorKey && (compacted.length === 0 || compacted[compacted.length - 1] === separatorKey)) {
                  continue
                }
                compacted.push(v)
              }
              while (compacted[0] === separatorKey) {
                compacted.shift()
              }
              while (compacted[compacted.length - 1] === separatorKey) {
                compacted.pop()
              }
              const rest = original.filter(v => {
                return v !== separatorKey && !compacted.includes(v)
              })
              result[service] = compacted.concat(rest)
            }
          }
        }

        const entries = Object.entries(result).map(([ key, children ]) => {
          return {
            key,
            children
          }
        })
        const order = sectionOrders[serviceName] || []
        const ordered = []
        for (const section of order) {
          const idx = entries.findIndex(e => {
            return e.key === section
          })
          if (idx !== -1) {
            ordered.push(entries.splice(idx, 1)[0])
          }
        }
        const finalList = ordered.concat(entries)

        return finalList.map(({ key, children }) => {
          return {
            key,
            icon: schemaIcons[key],
            children
          }
        })
      },

      defaults() {
        return getDefaults(serviceName)
      }
    },

    watch: {
      $route: {
        handler() {
          this.getKeyFromQuery()
        },
        deep: true
      },
      schema: {
        handler() {
          this.setSettingKey()
        },
        deep: true
      }
    },

    async mounted() {
      await this.getKeys()
      await this.getSettings()
      this.getKeyFromQuery()
    },

    methods: {
      getKeyFromQuery() {
        const { query } = this.$route
        if (query?.settingKey) {
          this.settingKey = query.settingKey
        }
        routerQueryReplace.call(this, {
          ...query,
          settingKey: this.settingKey
        })
      },

      setSettingKey() {
        if (this.settingKey) {
          const temp = cloneDeep(this.settingKey)
          this.settingKey = undefined
          this.$nextTick(() => {
            return this.settingKey = temp
          })
        } else {
          const keys = this.schema.map(item => {
            return item.key
          })
          if (!keys.includes(this.settingKey)) {
            this.settingKey = keys[0]
          }
        }
      },

      async getKeys() {
        if (!this.token) {
          return
        }
        try {
          this.loading.find = true
          const data = await request.find(joinURLPath(serviceName, 'keys', UserId))
          if (data) {
            if (!isEqual(this.keys, data)) {
              this.keys = data
            }
          }
        } catch (error) {
          globalErrorHandler(error)
        } finally {
          this.loading.find = false
        }
      },

      async getSettings() {
        if (!this.token) {
          return
        }
        this.loading.find = true
        try {
          const data = await request.find(joinURLPath(serviceName, UserId))
          if (data) {
            if (!isEqual(this.settings, data)) {
              this.settings = data
            }
          }
        } catch (error) {
          globalErrorHandler(error)
        }
        this.loading.find = false
      },

      async updateSetting(field, value) {
        this.$set(this.loading.update, field, true)
        try {
          const data = await request.update(joinURLPath(serviceName, UserId, field), { body: { value } })
          if (data) {
            this.settings[field] = data.value
            this.addSnackbar({
              type: 'success',
              text: this.getTranslate(`${serviceName}.snackbars.updated`)
            })
          }
        } catch (error) {
          globalErrorHandler(error)
        }
        this.$set(this.loading.update, field, false)
      }
    }
  }
}
